import { useEffect, useRef, useState, useCallback } from "react";
import SockJS from "sockjs-client";
import { Client, IMessage } from "@stomp/stompjs";

interface UseWebSocketProps<T> {
  serverUrl: string; // e.g. "http://localhost:8080/ws"
  topic: string; // topic to subscribe to
  onMessage: (data: T) => void; // handler for incoming messages
  // Nouvelle prop: fonction pour obtenir le token (peut être asynchrone)
  getToken?: () => Promise<string | undefined> | string | undefined;
  debug?: boolean; // optional: enable console logging
  reconnectDelay?: number; // optional: custom reconnect delay
}

interface WebSocketState {
  status: "Disconnected" | "Connecting" | "Connected";
}

export const useWebSocket = <T>({
  serverUrl,
  topic,
  onMessage,
  getToken, // Utilisez getToken à la place de token statique
  debug = false,
  reconnectDelay = 5000,
}: UseWebSocketProps<T>): WebSocketState => {
  const subscriptionRef = useRef<any | null>(null); // Pour stocker la référence de la souscription
  const [status, setStatus] =
    useState<WebSocketState["status"]>("Disconnected");
  const stompClientRef = useRef<Client | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Utilisation de useCallback pour stabiliser les handlers de message
  const handleIncomingMessage = useCallback(
    (message: IMessage) => {
      try {
        // Assurez-vous que la structure de message est bien { payload: T }
        const parsedData: T = JSON.parse(message.body).payload;
        if (debug) console.log("Incoming WebSocket message:", parsedData);
        onMessage(parsedData);
      } catch (error) {
        console.error("❌ Failed to parse WebSocket message:", error);
      }
    },
    [onMessage, debug]
  );

  // Fonction pour initialiser et activer la connexion
  const connect = useCallback(
    async (isReconnect = false) => {
      if (stompClientRef.current?.active) {
        if (debug)
          console.log("Client already active, skipping connection attempt.");
        return;
      }

      setStatus("Connecting");
      if (debug)
        console.log(
          `Attempting to connect (Reconnect: ${isReconnect}) to ${serverUrl} on topic ${topic}`
        );

      let currentToken: string | undefined;
      if (getToken) {
        try {
          currentToken = await Promise.resolve(getToken());
        } catch (error) {
          console.error("❌ Failed to get token:", error);
          // On pourrait décider de ne pas connecter si le token est critique et n'a pu être obtenu
          setStatus("Disconnected");
          return;
        }
      }

      // Annuler le timeout de reconnexion s'il existe
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      const socket = new SockJS(serverUrl);
      const stompClient = new Client({
        webSocketFactory: () => socket,
        // Ajout dynamique des headers d'autorisation
        connectHeaders: currentToken
          ? { Authorization: `Bearer ${currentToken}` }
          : {},
        debug: debug ? (str) => console.log(`[STOMP Debug] ${str}`) : undefined,
        // Désactiver le reconnectDelay interne de STOMP pour gérer la reconnexion nous-mêmes
        reconnectDelay: 5000,
        //heartbeatIncoming: 10000,
        //heartbeatOutgoing: 10000,

        onConnect: (frame) => {
          setStatus("Connected");
          if (debug)
            console.log(
              `✅ WebSocket connected. Subscribing to: ${topic}`,
              frame
            );

          // Subscribe to topic
          const subscription = stompClient.subscribe(
            topic,
            handleIncomingMessage
          );
          subscriptionRef.current = subscription;

          // LOG APRÈS AVOIR SOUSCRIT
          if (debug) {
            console.log(`📡 SUCCESSFULLY Subscribed to topic: ${topic}`);
            // La propriété 'id' de l'objet de souscription est souvent l'identifiant côté client
            console.log(`Subscription ID (client): ${subscription.id}`);
          }
        },

        onWebSocketClose: (event) => {
          setStatus("Disconnected");
          if (debug) console.log("🔌 WebSocket closed. Reconnecting...", event);
          // Tenter de se reconnecter après un délai pour l'écoute permanente
          if (reconnectTimeoutRef.current === null) {
            reconnectTimeoutRef.current = setTimeout(
              () => connect(true),
              reconnectDelay
            );
          }
        },

        onStompError: (frame) => {
          console.error("❌ Broker error:", frame.headers["message"], frame);
          // Déconnexion et déclenchement potentiel d'une reconnexion via onWebSocketClose
          stompClient.deactivate();
        },

        onWebSocketError: (event) => {
          console.error("❌ WebSocket error:", event);
          setStatus("Disconnected");
          // La fermeture du socket déclenchera onWebSocketClose, qui gérera la reconnexion
        },
      });

      stompClient.activate();
      stompClientRef.current = stompClient;
    },
    [serverUrl, topic, getToken, debug, reconnectDelay, handleIncomingMessage]
  );

  // Fonction pour déconnecter et nettoyer
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (stompClientRef.current) {
      stompClientRef.current.deactivate();
      stompClientRef.current = null;
      setStatus("Disconnected");
      if (debug) console.log("🧹 WebSocket cleanup/deactivate");
    }
  }, [debug]);

  // 1. Initialisation et écoute permanente
  useEffect(() => {
    if (!serverUrl || !topic) return;

    connect();

    return () => {
      // Nettoyage lors du démontage du composant
      disconnect();
    };
  }, [serverUrl, topic, connect, disconnect]);

  // 2. Gestion du rafraîchissement du token (si getToken change)
  // La dépendance `getToken` dans `connect` fait que `connect` change si `getToken` change.
  // L'effet principal dépend de `connect`, ce qui assure la reconnexion.
  // Cependant, pour forcer la reconnexion *si seul le token est censé avoir changé*,
  // il faut ajouter une logique de redémarrage.

  // Pour une **reconnexion forcée** lorsque l'information du token est mise à jour :
  const tokenRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Cette partie est cruciale pour le point 2: rafraîchissement du token
    const checkAndReconnect = async () => {
      if (!getToken) return;

      const newToken = await Promise.resolve(getToken());

      // Si le client est connecté et que le token a changé (ou est apparu/disparu)
      if (stompClientRef.current?.active && tokenRef.current !== newToken) {
        if (debug)
          console.log("🔑 Token changed, forcing WebSocket reconnect...");
        disconnect(); // Déconnecter l'ancien
        await new Promise((resolve) => setTimeout(resolve, 100)); // Petit délai de sécurité
        connect(); // Connecter avec le nouveau token
      }
      tokenRef.current = newToken;
    };

    // On pourrait ajouter un intervalle pour vérifier si le token a expiré
    // ou s'appuyer sur le mécanisme de déconnexion/reconnexion automatique
    // si l'expiration du token provoque une erreur STOMP/WebSocket.

    // Ici, on force la vérification si `getToken` change (bonne pratique React/hooks)
    // ou si on veut s'assurer que la première connexion utilise le token initial.
    if (getToken) {
      checkAndReconnect();
    } else {
      tokenRef.current = undefined; // S'assurer que le ref est à jour si pas de token
    }
  }, [getToken, disconnect, connect, debug]); // Dépendances essentielles

  // 3. Amélioration des bonnes pratiques (déjà intégrées):
  // - Utilisation de `useCallback` pour stabiliser les fonctions `connect`, `disconnect` et `handleIncomingMessage`.
  // - Gestion explicite du `reconnectTimeout` au lieu de s'appuyer uniquement sur le `reconnectDelay` interne de STOMP.
  // - Nettoyage complet dans le `return` de `useEffect`.
  // - Utilisation de `Promise.resolve(getToken())` pour gérer les fonctions `getToken` synchrones et asynchrones.

  return { status };
};
